import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import Webcam from 'react-webcam';
import { registerForEvent } from '../../api/registration';
import { getFormFields } from '../../api/activities';
import { FaUser, FaEnvelope, FaPhone, FaCheckCircle, FaCalendarAlt, FaHashtag, FaList, FaCheckSquare, FaComment, FaCamera, FaImage, FaTimes, FaMapMarkerAlt, FaChevronDown } from 'react-icons/fa';
import { useTheme } from '../../hooks/useTheme';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getFanMemberFromPath, fanPath } from '../../config/fanMembersConfig';

// Import location data
import provincesData from '../../data/provinces.json';
import citiesRegenciesJawaBali from '../../data/cities_regencies_jawa_bali.json';
import citiesRegenciesSumatera from '../../data/cities_regencies_sumatera.json';
import citiesRegenciesSulawesi from '../../data/cities_regencies_sulawesi.json';
import citiesRegenciesKalimantan from '../../data/cities_regencies_kalimantan.json';
import citiesRegenciesMalukuPapua from '../../data/cities_regencies_maluku_papua.json';
import malaysiaProvincesData from '../../data/malaysia_provinces.json';
import malaysiaCitiesData from '../../data/malaysia_cities.json';
import singaporeProvincesData from '../../data/singapore_provinces.json';
import singaporeCitiesData from '../../data/singapore_cities.json';
import bruneiProvincesData from '../../data/brunei_provinces.json';
import bruneiCitiesData from '../../data/brunei_cities.json';

// Configure dayjs for timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

// Autocomplete Component
const AutocompleteSelect = ({
  options,
  value,
  onChange,
  placeholder,
  searchValue,
  onSearchChange,
  isOpen,
  onToggle,
  onSelect,
  theme,
  disabled = false,
  required = false
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter options based on search value
  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchValue, options]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onToggle]);

  const handleInputClick = () => {
    if (!disabled) {
      onToggle(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        onSelect(filteredOptions[highlightedIndex]);
      } else if (filteredOptions.length > 0) {
        onSelect(filteredOptions[0]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1
      );
    } else if (e.key === 'Escape') {
      onToggle(false);
    }
  };

  const handleOptionClick = (option) => {
    onSelect(option);
    onToggle(false);
  };

  const handleOptionMouseEnter = (index) => {
    setHighlightedIndex(index);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    onSearchChange(value);
    if (value === '') {
      onChange(null); // Clear selected value in parent
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`flex items-center rounded-lg border transition-all duration-200 cursor-pointer ${theme === 'dark'
          ? 'bg-gray-700/50 border-gray-600 focus-within:border-primary-light focus-within:ring-2 focus-within:ring-primary-light/20'
          : 'bg-white border-gray-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={handleInputClick}
      >
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full bg-transparent outline-none text-base sm:text-lg px-4 py-3 ${theme === 'dark' ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
              } ${disabled ? 'cursor-not-allowed' : ''}`}
          />
          {searchValue && !disabled && (
            <button
              type="button"
              className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 focus:outline-none"
              onClick={e => {
                e.stopPropagation();
                onSearchChange('');
                onChange(null);
                inputRef.current?.focus();
              }}
              tabIndex={-1}
            >
              ×
            </button>
          )}
          {required && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">*</span>
          )}
        </div>
        <FaChevronDown
          className={`w-4 h-4 mr-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
        />
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-lg border shadow-lg ${theme === 'dark'
          ? 'bg-gray-700 border-gray-600'
          : 'bg-white border-gray-200'
          }`}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`px-4 py-3 cursor-pointer transition-colors ${theme === 'dark'
                  ? 'hover:bg-gray-600 text-white'
                  : 'hover:bg-gray-100 text-gray-900'
                  } ${value === option.value ? 'bg-primary/10 text-primary' : ''} ${index === highlightedIndex ? 'bg-primary/20' : ''
                  }`}
                onClick={() => handleOptionClick(option)}
                onMouseEnter={() => handleOptionMouseEnter(index)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className={`px-4 py-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RegistrationForm = ({ event }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
  });

  const [formFields, setFormFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showWebcam, setShowWebcam] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [provinceSearchValue, setProvinceSearchValue] = useState('');
  const [citySearchValue, setCitySearchValue] = useState('');
  const [isProvinceOpen, setIsProvinceOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [citiesData, setCitiesData] = useState(null);
  const [dropdownStates, setDropdownStates] = useState({});
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countrySearchValue, setCountrySearchValue] = useState('');
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const navigate = useNavigate();
  const { activityType } = useParams();
  const { theme } = useTheme();
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);

  // Use event.id instead of URL params for activity ID
  const activityId = event?.id;

  const videoConstraints = {
    width: 720,
    height: 720,
    facingMode: "user"
  };

  // Country options
  const countryOptions = [
    { value: 'indonesia', label: 'Indonesia' },
    { value: 'malaysia', label: 'Malaysia' },
    { value: 'singapore', label: 'Singapore' },
    { value: 'brunei', label: 'Brunei Darussalam' },
  ];

  // Memoize province options based on selected country
  const provinceOptions = useMemo(() => {
    let provinces = provincesData;
    if (selectedCountry === 'malaysia') provinces = malaysiaProvincesData;
    else if (selectedCountry === 'singapore') provinces = singaporeProvincesData;
    else if (selectedCountry === 'brunei') provinces = bruneiProvincesData;
    return provinces.map(province => ({
      value: province.id,
      label: province.nama
    }));
  }, [selectedCountry]);

  // Load cities data only when province is selected
  useEffect(() => {
    if (selectedProvince) {
      const loadCitiesData = async () => {
        setLoadingCities(true);
        try {
          let cities = [];

          // Load only the relevant cities data based on province
          const provinceId = parseInt(selectedProvince);

          // Determine which file to load based on country and province ID ranges
          if (selectedCountry === 'malaysia') {
            cities = malaysiaCitiesData.filter(city => city.provinsi_id === provinceId);
          } else if (selectedCountry === 'singapore') {
            cities = singaporeCitiesData.filter(city => city.provinsi_id === provinceId);
          } else if (selectedCountry === 'brunei') {
            cities = bruneiCitiesData.filter(city => city.provinsi_id === provinceId);
          } else {
            // Load Indonesia cities based on province ID ranges
            if (provinceId >= 11 && provinceId <= 17) {
              // Jawa & Bali (Banten, DKI Jakarta, Jawa Barat, Jawa Tengah, DIY, Jawa Timur, Bali)
              cities = citiesRegenciesJawaBali.filter(city => city.provinsi_id === provinceId);
            } else if (provinceId >= 1 && provinceId <= 10) {
              // Sumatera (Aceh through Bangka Belitung)
              cities = citiesRegenciesSumatera.filter(city => city.provinsi_id === provinceId);
            } else if (provinceId >= 25 && provinceId <= 30) {
              // Sulawesi (Sulawesi Utara through Gorontalo)
              cities = citiesRegenciesSulawesi.filter(city => city.provinsi_id === provinceId);
            } else if (provinceId >= 20 && provinceId <= 24) {
              // Kalimantan (Kalimantan Barat through Kalimantan Utara)
              cities = citiesRegenciesKalimantan.filter(city => city.provinsi_id === provinceId);
            } else if (provinceId >= 31 && provinceId <= 38) {
              // Maluku & Papua (Maluku through Papua Pegunungan)
              cities = citiesRegenciesMalukuPapua.filter(city => city.provinsi_id === provinceId);
            }
          }

          setCitiesData(cities);
        } catch (error) {
          console.error('Error loading cities data:', error);
          setCitiesData([]);
        } finally {
          setLoadingCities(false);
        }
      };

      loadCitiesData();
    } else {
      setCitiesData(null);
      setLoadingCities(false);
    }
  }, [selectedProvince, selectedCountry]);

  // Memoize city options
  const cityOptions = useMemo(() => {
    if (!citiesData) return [];
    return citiesData.map(city => ({
      value: city.nama,
      label: `${city.nama}`
    }));
  }, [citiesData]);

  const capturePhoto = React.useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc && currentImageField) {
      setFormData(prev => ({
        ...prev,
        [currentImageField]: imageSrc
      }));
      setShowWebcam(false);
    }
  }, [currentImageField]);

  const openCamera = (fieldName) => {
    setCurrentImageField(fieldName);
    setShowWebcam(true);
  };

  useEffect(() => {
    const fetchFormFields = async () => {
      try {
        const response = await getFormFields(activityId);
        setFormFields(response?.data || []);
      } catch (error) {
        console.error('Error fetching form fields:', error);
      }
    };

    fetchFormFields();
  }, [activityId]);

  // Handle country selection
  const handleCountrySelect = (country) => {
    if (!country) {
      setSelectedCountry('');
      setCountrySearchValue('');
      setSelectedProvince('');
      setProvinceSearchValue('');
      setSelectedCity('');
      setCitySearchValue('');
      setFormData(prev => ({
        ...prev,
        negara: '',
        kota: ''
      }));
      return;
    }
    setSelectedCountry(country.value);
    setCountrySearchValue(country.label);
    setSelectedProvince('');
    setProvinceSearchValue('');
    setSelectedCity('');
    setCitySearchValue('');
    setFormData(prev => ({
      ...prev,
      negara: country.value,
      kota: '' // Reset kota field when country changes
    }));
  };

  // Handle province selection
  const handleProvinceSelect = (province) => {
    if (!province) {
      setSelectedProvince('');
      setProvinceSearchValue('');
      setSelectedCity('');
      setCitySearchValue('');
      setFormData(prev => ({
        ...prev,
        kota: ''
      }));
      return;
    }
    setSelectedProvince(province.value);
    setProvinceSearchValue(province.label);
    setSelectedCity('');
    setCitySearchValue('');
    setFormData(prev => ({
      ...prev,
      kota: '' // Reset kota field when province changes
    }));
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    if (!city) {
      setSelectedCity('');
      setCitySearchValue('');
      setFormData(prev => ({
        ...prev,
        kota: ''
      }));
      return;
    }
    setSelectedCity(city.value);
    setCitySearchValue(city.label);
    setFormData(prev => ({
      ...prev,
      kota: city.value
    }));
  };

  // Helper functions for dropdown state management
  const getDropdownState = useCallback((fieldName) => {
    if (!dropdownStates[fieldName]) {
      dropdownStates[fieldName] = {
        searchValue: '',
        isOpen: false
      };
    }
    return dropdownStates[fieldName];
  }, [dropdownStates]);

  const updateDropdownState = useCallback((fieldName, updates) => {
    setDropdownStates(prev => ({
      ...prev,
      [fieldName]: {
        ...getDropdownState(fieldName),
        ...updates
      }
    }));
  }, [getDropdownState]);

  // Initialize search values when form data changes
  useEffect(() => {
    // Set province search value if province is selected
    if (selectedProvince && !provinceSearchValue) {
      const province = provincesData.find(p => p.id === parseInt(selectedProvince));
      if (province) {
        setProvinceSearchValue(province.nama);
      }
    }

    // Set city search value if city is selected
    if (selectedCity && !citySearchValue && citiesData) {
      const city = citiesData.find(c => c.nama === selectedCity);
      if (city) {
        setCitySearchValue(`${city.nama} (${city.tipe})`);
      }
    }

    // Initialize other dropdown fields
    formFields.forEach(field => {
      if (field.typeId === 'dropdown' && field.name !== 'Kota' && formData[field.name]) {
        const currentState = getDropdownState(field.name);
        if (!currentState.searchValue) {
          updateDropdownState(field.name, { searchValue: formData[field.name] });
        }
      }
    });
  }, [selectedProvince, selectedCity, citiesData, provinceSearchValue, citySearchValue, formFields, formData, getDropdownState, updateDropdownState]);

  const handleDropdownSelect = (fieldName, option) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: option ? option.value : ''
    }));
    updateDropdownState(fieldName, {
      searchValue: option ? option.label : '',
      isOpen: false
    });
  };

  // Simple validation
  const isNameValid = formData.name.trim().length > 2;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const isPhoneValid = /^\+?\d{7,15}$/.test(formData.phoneNumber);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    // Validate required fields manually
    const missingRequiredFields = formFields.filter(field => {
      if (!field.isRequired) return false;

      // Special handling for Kota field - check both "Kota" and "kota" keys
      let value;
      if (field.name === 'Kota') {
        value = formData[field.name] || formData['kota'];
      } else {
        value = formData[field.name];
      }

      if (field.typeId === 'checkbox') {
        return !value || value.length === 0;
      }

      return !value || (typeof value === 'string' && value.trim() === '');
    });

    if (missingRequiredFields.length > 0) {
      setMessage({
        text: `Please fill in all required fields: ${missingRequiredFields.map(f => f.name).join(', ')}`,
        type: 'error'
      });
      setLoading(false);
      return;
    }

    try {
      const formSubmissionValues = formFields.map((field) => {
        let value;

        if (field.typeId === 'checkbox') {
          // Handle checkbox fields
          const checkboxValues = formData[field.name] || [];
          value = checkboxValues.length > 0 ? checkboxValues.join('|') : null;
        } else {
          // Handle other field types
          let fieldValue;
          
          // Special handling for Kota field - check both "Kota" and "kota" keys
          if (field.name === 'Kota') {
            fieldValue = formData[field.name] || formData['kota'];
          } else {
            fieldValue = formData[field.name];
          }

          // If field is not required and value is empty, use null
          if (!field.isRequired && (!fieldValue || fieldValue.trim() === '')) {
            value = null;
          } else {
            value = fieldValue || '';
          }
        }

        return {
          formFieldId: field.id,
          value: value
        };
      });

      const fanMember = getFanMemberFromPath(window.location.pathname);
      console.log('Registering for activity:', { activityId, activityType, fanMember: fanMember?.slug });
      const response = await registerForEvent(activityId, { 
        ...formData, 
        negara: selectedCountry,
        formSubmissionValues: formSubmissionValues 
      });
      console.log('Registration response:', response);
      if (response.data.id) {
        // Set cookies to expire in 1 hour WIB time
        const wibExpiry = dayjs().tz('Asia/Jakarta').add(1, 'hour').toDate();
        Cookies.set('registrationId', response.data.id, { expires: wibExpiry });
        Cookies.set('phoneNumber', response.data.user.phoneNumber, { expires: wibExpiry });
        
        if (response.data.isVerified) {
          if (fanMember) {
            navigate(fanPath(fanMember.slug, 'member'));
          } else {
            const routeActivityType = activityType || 'fan';
            navigate(`/${routeActivityType}/${activityId}/member`);
          }
        } else {
          if (fanMember) {
            navigate(fanPath(fanMember.slug, 'verify'));
          } else {
            const routeActivityType = activityType || 'fan';
            navigate(`/${routeActivityType}/${activityId}/verify`);
          }
        }
      }
      throw new Error('Registration failed. Please try again.');
    } catch (error) {
      setMessage({
        text: error.response?.data?.error || error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageCapture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [e.target.name]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const renderWebcamModal = () => {
    if (!showWebcam) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`relative bg-white rounded-lg p-4 w-full max-w-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
          <button
            onClick={() => setShowWebcam(false)}
            className={`absolute top-2 right-2 p-2 rounded-full ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <FaTimes className="w-5 h-5" />
          </button>

          <div className="w-full aspect-square overflow-hidden rounded-lg mb-4">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={capturePhoto}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${theme === 'dark'
                ? 'bg-primary-light hover:bg-primary text-white'
                : 'bg-primary hover:bg-primary-dark text-white'
                }`}
            >
              <FaCamera className="w-5 h-5" />
              Capture Photo
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFormField = (field) => {
    const commonClasses = `w-full bg-transparent outline-none text-base sm:text-lg px-4 py-3 ${theme === 'dark' ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
      }`;

    const containerClasses = `flex items-center rounded-lg border transition-all duration-200 ${theme === 'dark'
      ? 'bg-gray-700/50 border-gray-600 focus-within:border-primary-light focus-within:ring-2 focus-within:ring-primary-light/20'
      : 'bg-white border-gray-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
      }`;

    const getFieldIcon = () => {
      // Special case for Kota field
      if (field.name === 'Kota') {
        return <FaMapMarkerAlt className="w-5 h-5" />;
      }

      switch (field.typeId) {
        case 'text':
          return <FaComment className="w-5 h-5" />;
        case 'number':
          return <FaHashtag className="w-5 h-5" />;
        case 'datetime':
          return <FaCalendarAlt className="w-5 h-5" />;
        case 'dropdown':
          return <FaList className="w-5 h-5" />;
        case 'checkbox':
          return <FaCheckSquare className="w-5 h-5" />;
        case 'image':
          return <FaImage className="w-5 h-5" />;
        default:
          return <FaUser className="w-5 h-5" />;
      }
    };

    const renderFieldContent = () => {
      switch (field.typeId) {
        case 'text':
          return (
            <div className="flex-1 relative">
              <input
                type="text"
                id={field.name}
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleChange}
                required={field.isRequired}
                placeholder={field.name}
                className={commonClasses}
              />
              {field.isRequired && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">*</span>
              )}
            </div>
          );

        case 'number':
          return (
            <div className="flex-1 relative">
              <input
                type="number"
                id={field.name}
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleChange}
                required={field.isRequired}
                placeholder={field.name}
                className={commonClasses}
              />
              {field.isRequired && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">*</span>
              )}
            </div>
          );

        case 'datetime':
          return (
            <div className="flex flex-col gap-3 px-4 py-3 w-full">
              <label
                className={`text-base font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
              >
                {field.name}
                {field.isRequired && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="relative">
                <input
                  type="date"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                  required={field.isRequired}
                  className={commonClasses}
                />
                {field.isRequired && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">*</span>
                )}
              </div>
            </div>
          );

        case 'dropdown':
          // Special case for Kota field - cascading dropdown
          if (field.name === 'Kota') {
            return (
              <div className="flex flex-col gap-3 px-4 py-3 w-full">
                <label
                  className={`text-base font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}
                >
                  {field.name}
                  {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>

                {/* Country Dropdown */}
                <div className="relative">
                  <AutocompleteSelect
                    options={countryOptions}
                    value={selectedCountry}
                    onChange={handleCountrySelect}
                    placeholder="Pilih Negara"
                    searchValue={countrySearchValue}
                    onSearchChange={setCountrySearchValue}
                    isOpen={isCountryOpen}
                    onToggle={setIsCountryOpen}
                    onSelect={handleCountrySelect}
                    theme={theme}
                    disabled={loading}
                    required={field.isRequired}
                  />
                </div>

                {/* Province Dropdown */}
                {selectedCountry && (
                  <div className="relative">
                    <AutocompleteSelect
                      options={provinceOptions}
                      value={selectedProvince}
                      onChange={handleProvinceSelect}
                      placeholder="Pilih Provinsi"
                      searchValue={provinceSearchValue}
                      onSearchChange={setProvinceSearchValue}
                      isOpen={isProvinceOpen}
                      onToggle={setIsProvinceOpen}
                      onSelect={handleProvinceSelect}
                      theme={theme}
                      disabled={loading}
                      required={field.isRequired}
                    />
                  </div>
                )}

                {/* City/Regency Dropdown */}
                {selectedProvince && (
                  <div className="relative">
                    <AutocompleteSelect
                      options={cityOptions}
                      value={selectedCity}
                      onChange={handleCitySelect}
                      placeholder={loadingCities ? "Loading cities..." : "Pilih Kota/Kabupaten"}
                      searchValue={citySearchValue}
                      onSearchChange={setCitySearchValue}
                      isOpen={isCityOpen}
                      onToggle={setIsCityOpen}
                      onSelect={handleCitySelect}
                      theme={theme}
                      disabled={loading || loadingCities}
                      required={field.isRequired}
                    />
                  </div>
                )}
              </div>
            );
          }

          // Regular dropdown for other fields - convert to autocomplete
          const dropdownOptions = field.options?.map(option => ({
            value: option,
            label: option
          })) || [];

          const dropdownState = getDropdownState(field.name);

          return (
            <div className="flex-1 relative">
              <AutocompleteSelect
                options={dropdownOptions}
                value={formData[field.name] || ''}
                onChange={(option) => handleDropdownSelect(field.name, option)}
                placeholder={`Pilih ${field.name}`}
                searchValue={dropdownState.searchValue}
                onSearchChange={(value) => updateDropdownState(field.name, { searchValue: value })}
                isOpen={dropdownState.isOpen}
                onToggle={(isOpen) => updateDropdownState(field.name, { isOpen })}
                onSelect={(option) => handleDropdownSelect(field.name, option)}
                theme={theme}
                disabled={loading}
                required={field.isRequired}
              />
            </div>
          );

        case 'checkbox':
          return (
            <div className="flex flex-col gap-3 px-4 py-3 w-full">
              <label
                className={`text-base font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}
              >
                {field.name}
                {field.isRequired && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {field.options?.map((option) => {
                  const isSelected = formData[field.name]?.includes(option) || false;
                  return (
                    <div key={option} className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          const currentValues = formData[field.name] || [];
                          const newValues = isSelected
                            ? currentValues.filter(val => val !== option)
                            : [...currentValues, option];
                          setFormData(prev => ({
                            ...prev,
                            [field.name]: newValues
                          }));
                        }}
                        className={`flex items-center gap-3 w-full p-3 rounded-lg border-2 transition-all duration-200 text-left ${isSelected
                          ? theme === 'dark'
                            ? 'bg-primary-light/20 border-primary-light text-primary-light hover:bg-primary-light/30'
                            : 'bg-primary/10 border-primary text-primary hover:bg-primary/20'
                          : theme === 'dark'
                            ? 'bg-gray-700/50 border-gray-600 text-gray-300 hover:border-gray-500 hover:bg-gray-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                      >
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${isSelected
                          ? theme === 'dark'
                            ? 'bg-primary-light border-primary-light'
                            : 'bg-primary border-primary'
                          : theme === 'dark'
                            ? 'border-gray-500 bg-transparent'
                            : 'border-gray-400 bg-transparent'
                          }`}>
                          {isSelected && (
                            <div className={`w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-white' : 'bg-white'
                              }`} />
                          )}
                        </div>
                        <span className="text-sm sm:text-base font-medium">
                          {option}
                        </span>
                      </button>
                      {/* Hidden input for form validation */}
                      <input
                        type="checkbox"
                        id={`${field.name}-${option}`}
                        name={field.name}
                        value={option}
                        checked={isSelected}
                        onChange={() => { }} // Handled by button click
                        required={field.isRequired && (!formData[field.name] || formData[field.name].length === 0)}
                        className="hidden"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );

        case 'image':
          return (
            <div className="flex-1 relative">
              <input
                type="file"
                id={field.name}
                name={field.name}
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageCapture}
                className="hidden"
              />
              <div className="flex flex-col gap-3 px-4 py-3 w-full">
                <label
                  className={`text-base font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}
                >
                  {field.name}
                  {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => openCamera(field.name)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${theme === 'dark'
                      ? 'bg-gray-600 hover:bg-gray-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                  >
                    <FaCamera className="w-4 h-4" />
                    Take Photo
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${theme === 'dark'
                      ? 'bg-gray-600 hover:bg-gray-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                  >
                    <FaImage className="w-4 h-4" />
                    Choose File
                  </button>
                </div>
                {formData[field.name] && (
                  <div className="mt-2">
                    <img
                      src={formData[field.name]}
                      alt="Selected"
                      className="max-w-full h-32 object-contain rounded-lg"
                    />
                  </div>
                )}
                {field.isRequired && !formData[field.name] && (
                  <div className="text-red-500 text-sm mt-1">
                    {field.name} is required
                  </div>
                )}
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    if (field.typeId === 'checkbox' || field.name === 'Kota') {
      return (
        <div key={field.id} className={containerClasses}>
          <span className={`p-3 sm:p-4 ${theme === 'dark' ? 'text-primary-light' : 'text-primary'
            }`}>
            {getFieldIcon()}
          </span>
          {renderFieldContent()}
        </div>
      );
    }

    return (
      <div key={field.id} className={containerClasses}>
        <span className={`p-3 sm:p-4 ${theme === 'dark' ? 'text-primary-light' : 'text-primary'
          }`}>
          {getFieldIcon()}
        </span>
        {renderFieldContent()}
        {formData[field.name] && field.typeId !== 'checkbox' && (
          <FaCheckCircle className={`w-5 h-5 mr-3 text-green-500 animate-fadeIn`} />
        )}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col items-center p-2 sm:p-3 md:p-4">
      {renderWebcamModal()}
      {message.text && (
        <div className={`w-full p-4 rounded-lg mb-6 font-medium text-center animate-fadeIn ${message.type === 'error'
          ? theme === 'dark'
            ? 'bg-red-900/20 text-red-400 border border-red-900/30'
            : 'bg-red-50 text-red-600 border border-red-200'
          : theme === 'dark'
            ? 'bg-green-900/20 text-green-400 border border-green-900/30'
            : 'bg-green-50 text-green-600 border border-green-200'
          }`}>
          {message.text}
        </div>
      )}
      <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4 sm:gap-5 md:gap-6" autoComplete="off">
        <div className={`flex items-center rounded-lg border transition-all duration-200 ${theme === 'dark'
          ? 'bg-gray-700/50 border-gray-600 focus-within:border-primary-light focus-within:ring-2 focus-within:ring-primary-light/20'
          : 'bg-white border-gray-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
          }`}>
          <span className={`p-3 sm:p-4 ${theme === 'dark' ? 'text-primary-light' : 'text-primary'
            }`}>
            <FaUser className="w-5 h-5" />
          </span>
          <div className="flex-1 relative">
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Full Name"
              className={`w-full bg-transparent outline-none text-base sm:text-lg ${theme === 'dark' ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                }`}
              autoComplete="name"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">*</span>
          </div>
          {isNameValid && (
            <FaCheckCircle className={`w-5 h-5 mr-3 text-green-500 animate-fadeIn`} />
          )}
        </div>

        <div className={`flex items-center rounded-lg border transition-all duration-200 ${theme === 'dark'
          ? 'bg-gray-700/50 border-gray-600 focus-within:border-primary-light focus-within:ring-2 focus-within:ring-primary-light/20'
          : 'bg-white border-gray-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
          }`}>
          <span className={`p-3 sm:p-4 ${theme === 'dark' ? 'text-primary-light' : 'text-primary'
            }`}>
            <FaEnvelope className="w-5 h-5" />
          </span>
          <div className="flex-1 relative">
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Email Address"
              className={`w-full bg-transparent outline-none text-base sm:text-lg ${theme === 'dark' ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                }`}
              autoComplete="email"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">*</span>
          </div>
          {isEmailValid && (
            <FaCheckCircle className={`w-5 h-5 mr-3 text-green-500 animate-fadeIn`} />
          )}
        </div>

        <div className={`flex items-center rounded-lg border transition-all duration-200 ${theme === 'dark'
          ? 'bg-gray-700/50 border-gray-600 focus-within:border-primary-light focus-within:ring-2 focus-within:ring-primary-light/20'
          : 'bg-white border-gray-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
          }`}>
          <span className={`p-3 sm:p-4 ${theme === 'dark' ? 'text-primary-light' : 'text-primary'
            }`}>
            <FaPhone className="w-5 h-5" />
          </span>
          <div className="flex-1 relative">
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              placeholder="Phone Number"
              className={`w-full bg-transparent outline-none text-base sm:text-lg ${theme === 'dark' ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                }`}
              autoComplete="tel"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">*</span>
          </div>
          {isPhoneValid && (
            <FaCheckCircle className={`w-5 h-5 mr-3 text-green-500 animate-fadeIn`} />
          )}
        </div>

        {/* Render dynamic form fields */}
        {formFields.map((field) => renderFormField(field))}

        <button
          type="submit"
          disabled={loading || !isNameValid || !isEmailValid || !isPhoneValid}
          className={`w-full py-3 sm:py-4 rounded-lg font-semibold text-white transition-all duration-200 
            disabled:opacity-70 disabled:cursor-not-allowed
            ${theme === 'dark'
              ? 'bg-gradient-secondary hover:bg-gradient-secondary-dark'
              : 'bg-primary hover:bg-primary-dark'
            }`}
        >
          {loading ? (
            <div className="flex justify-center">
              <BeatLoader color="#ffffff" size={10} />
            </div>
          ) : (
            'Create an account'
          )}
        </button>

        <div className={`text-center text-sm sm:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
          By signing up, you agree to our{' '}
          <button
            type="button"
            className={`font-medium underline transition-colors ${theme === 'dark' ? 'text-primary-light hover:text-primary' : 'text-primary hover:text-primary-dark'
              }`}
          >
            Terms and Conditions of Use
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;